import { AddIcon } from '@chakra-ui/icons';
import {
	Button,
	Flex,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';

import { ButtonComponent } from '../buttons/ButtonComponent';
import { TooltipStandard } from '../tooltip/TooltipStandard';

interface ICheckTableActionsEdit {
	massEditingActions: (filters: any) => {};
	allSelectedItens: boolean;
	hasSelectedItems: boolean;
	getSelectedIds: () => number[];
	totalRegisters: number;
	dataAction: any;
	onToggleSelectAll?: () => void;
}

const CheckTableActionsEdit = ({
	massEditingActions,
	allSelectedItens,
	hasSelectedItems,
	getSelectedIds,
	totalRegisters,
	dataAction,
	onToggleSelectAll,
}: ICheckTableActionsEdit) => {
	const { isOpen, onClose, onOpen } = useDisclosure();

	const selectedIds = getSelectedIds();
	const [messageModal, setMessageModal] = useState<string>('');

	function openModal(value: string) {
		setMessageModal(value);
		onOpen();
	}

	function onAction() {
		const action = messageModal === 'add' ? true : false;

		let actionFilters: { todos: boolean; ids: number[]; action: boolean };
		actionFilters = {
			todos: allSelectedItens,
			ids: selectedIds,
			action: action,
		};

		massEditingActions(actionFilters);

		onClose();
	}

	const menuOptions = useCallback(() => {
		return (
			dataAction.menuOptions?.map(option => {
				return (
					<MenuItem
						key={option.value}
						onClick={() => openModal(option.value)}
					>
						<Text color='easyBLUE.300'>{option.label}</Text>
					</MenuItem>
				);
			}) || []
		);
	}, [dataAction]);

	function renderButton() {
		const hasItems = hasSelectedItems || allSelectedItens;
		const menuButton = (
			<MenuButton
				data-test={'menu_button-opcoes'}
				as={Button}
				rightIcon={<AddIcon boxSize={3} />}
				size='large'
				fontWeight='100'
				px='30px'
				py='8px'
				border='1px solid'
				borderRadius='100px'
				borderColor={hasItems ? 'easyBLUE.150' : 'gray.200'}
				fontSize='16px'
				color={hasItems ? 'easyBLUE.300' : 'gray.400'}
				_hover={{ bg: hasItems ? '#FAFCFE' : 'gray.50' }}
				_focus={{ bg: 'white' }}
				_active={{
					bg: hasItems ? '#E2E8F0' : 'gray.100',
					boxShadow: 'none',
				}}
				isDisabled={!hasItems}
			>
				<Text>Opções</Text>
			</MenuButton>
		);

		return (
			<Menu>
				{hasItems ? (
					menuButton
				) : (
					<TooltipStandard label='Por favor, selecione pelo menos um item!'>
						{menuButton}
					</TooltipStandard>
				)}
				{hasItems && <MenuList>{menuOptions()}</MenuList>}
			</Menu>
		);
	}

	const selectedItems = useCallback(() => {
		if (allSelectedItens) {
			return totalRegisters;
		} else if (selectedIds?.length) {
			return selectedIds?.length;
		}
		return 0;
	}, [allSelectedItens, selectedIds, totalRegisters]);

	function renderAllSelected() {
		if (allSelectedItens) {
			return (
				<Flex
					alignItems='center'
					ml='20px'
				>
					<Text>
						Todos os {selectedItems()} registros estão selecionados.
					</Text>
					<ButtonComponent
						type='ghost'
						title={`Remover seleção dos ${totalRegisters} registros.`}
						border='none'
						textColor='#0296f2'
						onClick={onToggleSelectAll}
						px={2}
					/>
				</Flex>
			);
		} else if (hasSelectedItems && !allSelectedItens) {
			return (
				<Flex
					alignItems='center'
					ml='20px'
				>
					<Text>{selectedIds?.length} registros selecionados.</Text>
					<ButtonComponent
						type='ghost'
						title={`Selecionar todos os ${totalRegisters} registros.`}
						border='none'
						textColor='#0296f2'
						onClick={onToggleSelectAll}
						px={2}
					/>
				</Flex>
			);
		}

		return null;
	}

	function renderModal() {
		const isAddMessage = messageModal === 'add';
		const header = isAddMessage
			? dataAction?.modalTitleAdd
			: dataAction?.modalTitleRemove;
		const body = isAddMessage
			? dataAction?.modalTextAdd
			: dataAction?.modalTextRemove;
		const buttonConfirm = dataAction?.buttonText
			? dataAction?.buttonText
			: 'Editar';
		return (
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				onEsc={onClose}
				isCentered
			>
				<ModalOverlay />
				<ModalContent
					borderRadius={20}
					pt='20px'
				>
					<ModalHeader textColor={'easyBLUE.300'}>
						{header}
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text
							color='gray.500'
							fontSize='sm'
							fontWeight={200}
						>
							{body} <br /> <br />
							{`Total de itens selecionados: ${
								allSelectedItens
									? totalRegisters
									: selectedIds?.length
							}`}
						</Text>
					</ModalBody>
					<ModalFooter>
						<Flex gap={1}>
							<ButtonComponent
								data-test='button-cancelar-modal'
								type='ghost'
								title='Cancelar'
								onClick={onClose}
							/>
							<ButtonComponent
								data-test='button-sim-modal'
								type='primary'
								title={buttonConfirm}
								onClick={onAction}
							/>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	}

	return (
		<Flex
			direction='row'
			ml='20px'
			mr='20px'
		>
			{renderButton()}
			{renderAllSelected()}
			{renderModal()}
		</Flex>
	);
};

export default CheckTableActionsEdit;
